/**
 * FeedbackSection - Thumbs up/down feedback
 *
 * Extracted from ModernResultsFull.tsx for Phase 3 Mobile Optimization.
 */

import { View, Pressable } from 'react-native';
import { useState, useCallback } from 'react';
import { TierBody } from '@dramadetector/ui';

import { sharedStyles } from '../sharedStyles';

export interface FeedbackSectionProps {
  /** Callback when feedback is submitted */
  onFeedback?: (rating: 'up' | 'down') => void;
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Feedback section with thumbs up/down buttons
 */
export function FeedbackSection({ onFeedback, t, testID }: FeedbackSectionProps) {
  const [selected, setSelected] = useState<'up' | 'down' | null>(null);

  const handleFeedback = useCallback(
    (rating: 'up' | 'down') => {
      setSelected(rating);
      onFeedback?.(rating);
    },
    [onFeedback]
  );

  return (
    <View style={sharedStyles.glassCard} testID={testID}>
      <TierBody
        size="sm"
        color="text-gray-600"
        style={{ textAlign: 'center', marginBottom: 12 }}
      >
        {t('home.modern.was_helpful', { defaultValue: 'Was this analysis helpful?' })}
      </TierBody>
      <View style={sharedStyles.feedbackRow}>
        <Pressable
          style={[
            sharedStyles.feedbackButton,
            selected === 'up' && sharedStyles.feedbackButtonActive,
          ]}
          onPress={() => handleFeedback('up')}
          accessibilityRole="button"
          accessibilityLabel={t('home.modern.helpful_yes', { defaultValue: 'Yes, helpful' })}
        >
          <TierBody size="lg">👍</TierBody>
        </Pressable>
        <Pressable
          style={[
            sharedStyles.feedbackButton,
            selected === 'down' && sharedStyles.feedbackButtonActive,
          ]}
          onPress={() => handleFeedback('down')}
          accessibilityRole="button"
          accessibilityLabel={t('home.modern.helpful_no', { defaultValue: 'No, not helpful' })}
        >
          <TierBody size="lg">👎</TierBody>
        </Pressable>
      </View>
    </View>
  );
}
